import React, { useState } from 'react';
import './MarkerPopup.css';
import { CSS_STATUS_CLASS, CONNECTION_STATUS, COMPONENT_STATUS } from '../../context/enums';
import { getStatusText } from '../../utils/systemStatusUtils';
import { formatTimeReported, formatDeviceTimestamp } from '../../utils/timeReportedUtils';
import { getSensorName, getRelayName } from '../../utils/deviceNameUtils';
import ConfirmationPopup from '../ConfirmationPopup';

const ADMIN_CONTROLS_TITLE = 'Admin Controls';
export const MANUAL_RESET_TITLE = 'Manual Reset';
export const MANUAL_RESET_MESSAGE = 'Manual Reset will deactivate fire suppression, clear the visible cooldown, and resume autonomous monitoring immediately. Active hazard sensors may reactivate suppression.';

/** Maps raw sensor/relay status strings to display label and CSS class. */
const DEVICE_STATUS_BADGE = {
  [COMPONENT_STATUS.ACTIVE]:   { label: 'Alert', cssClass: CSS_STATUS_CLASS.ALERT },
  [COMPONENT_STATUS.INACTIVE]: { label: 'Error', cssClass: CSS_STATUS_CLASS.ERROR },
  [COMPONENT_STATUS.STANDBY]:  { label: 'Ready', cssClass: CSS_STATUS_CLASS.READY },
};
const DEVICE_STATUS_BADGE_DEFAULT = { label: 'Unknown', cssClass: CSS_STATUS_CLASS.DISCONNECTED };

/** Maps sharedMode values to CSS_STATUS_CLASS tokens for the system row badge. */
const SYSTEM_STATUS_CSS_CLASS = {
  OFF:           CSS_STATUS_CLASS.READY,
  PRIMING:       CSS_STATUS_CLASS.ALERT,
  ACTIVATING:    CSS_STATUS_CLASS.ALERT,
  DEACTIVATING:  CSS_STATUS_CLASS.ALERT,
  ON:            CSS_STATUS_CLASS.ERROR,
  ERROR:         CSS_STATUS_CLASS.ERROR,
  UNINITIALIZED: CSS_STATUS_CLASS.DISCONNECTED,
};

/**
 * Returns connection dot modifier class and label text for a connection string.
 * @param {string|undefined} connection
 * @returns {{ dotClass: string, label: string }}
 */
function connectionDisplay(connection) {
  const connected = connection?.toLowerCase() === CONNECTION_STATUS.CONNECTED;
  return {
    dotClass: connected ? 'popup-conn-dot--connected' : 'popup-conn-dot--disconnected',
    label: connected ? 'Connected' : 'Disconnected',
  };
}

/**
 * Derives the system status badge from a systemStatus object.
 * @param {Object|null} systemStatus
 * @returns {{ label: string, cssClass: string }}
 */
function deriveSystemBadge(systemStatus) {
  if (!systemStatus) {
    return { label: getStatusText(null), cssClass: CSS_STATUS_CLASS.DISCONNECTED };
  }
  return {
    label: getStatusText(systemStatus),
    cssClass: SYSTEM_STATUS_CSS_CLASS[systemStatus.sharedMode] ?? CSS_STATUS_CLASS.DISCONNECTED,
  };
}

/**
 * Derives CBW-dependent fields from a controlByWebStatus object.
 * CBW sensor data values are always 0/1 and duplicate the status badge, so they
 * are stripped here rather than displayed in DeviceRow.
 * @param {Object|null} cbw
 * @returns {{ systemConnection: string, cbwSensors: Array, relays: Array }}
 */
function deriveCbwFields(cbw) {
  if (!cbw) {
    return { systemConnection: CONNECTION_STATUS.DISCONNECTED, cbwSensors: [], relays: [] };
  }
  return {
    systemConnection: cbw.connection,
    cbwSensors: cbw.sensors.map(sensor => ({ ...sensor, data: undefined })),
    relays: cbw.relays,
  };
}

/**
 * Derives all data needed to render the popup body from hydroSystemStatus.
 * @param {Object|null} hydroSystemStatus
 * @returns {{ systemBadge: {label: string, cssClass: string}, systemConnection: string, systemTimeReported: Date|null, allSensors: Array, relays: Array }}
 */
function deriveSystemProps(hydroSystemStatus) {
  if (!hydroSystemStatus) {
    return {
      systemBadge: { label: 'Offline', cssClass: CSS_STATUS_CLASS.DISCONNECTED },
      systemConnection: CONNECTION_STATUS.DISCONNECTED,
      systemTimeReported: null,
      allSensors: [],
      relays: [],
    };
  }
  const { systemConnection, cbwSensors, relays } = deriveCbwFields(hydroSystemStatus.controlByWebStatus);
  const externalSensors = hydroSystemStatus.externals ? hydroSystemStatus.externals.sensors : [];
  return {
    systemBadge: deriveSystemBadge(hydroSystemStatus.systemStatus),
    systemConnection,
    systemTimeReported: hydroSystemStatus.timeReported ?? null,
    allSensors: [...cbwSensors, ...externalSensors],
    relays,
  };
}

/**
 * Renders a single device row (system, sensor, or relay).
 * @param {{ id: string, connection: string|undefined, statusBadge: {label: string, cssClass: string}, timestamp: Date|null, formatTimestamp: Function, data: string|undefined }} props
 */
function DeviceRow({ id, connection, statusBadge, timestamp, formatTimestamp, data }) {
  const { dotClass, label } = connectionDisplay(connection);
  return (
    <div className="popup-device-row">
      <div className="popup-device-name">{id}</div>
      <div className="popup-conn-cell">
        <span className={`popup-conn-dot ${dotClass}`} />
        <span className="popup-conn-label">{label}</span>
      </div>
      <div className="popup-status-cell">
        <span className={`popup-status-badge ${statusBadge.cssClass}`}>{statusBadge.label}</span>
        <div className="popup-row-timestamp">{formatTimestamp(timestamp ?? null)}</div>
      </div>
      {data != null && data !== '' && <div className="popup-data-row"><span className="popup-data-label">Data:</span> {data}</div>}
    </div>
  );
}

/**
 * Renders a labeled section (Sensors or Relays) with a divider and one DeviceRow per device.
 * Renders nothing when devices is empty.
 * @param {{ label: string, devices: Array<{id: string, connection: string, status: string, timeLastUpdate: Date|null, data: string|undefined}>, resolveName: Function }} props
 */
function DeviceSection({ label, devices, resolveName }) {
  if (devices.length === 0) return null;
  return (
    <>
      <div className="popup-section-label">{label}</div>
      <div className="popup-section-divider" />
      {devices.map(device => (
        <DeviceRow
          key={device.id}
          id={resolveName(device.id)}
          connection={device.connection}
          statusBadge={DEVICE_STATUS_BADGE[device.status?.toLowerCase()] ?? DEVICE_STATUS_BADGE_DEFAULT}
          timestamp={device.timeLastUpdate}
          formatTimestamp={formatDeviceTimestamp}
          data={device.data}
        />
      ))}
    </>
  );
}

/**
 * Bottom-sheet popup that displays HydroSystemStatus details for a selected fire controller marker.
 * @param {Object} props - Component properties.
 * @param {Object|null} props.markerEntry - Entry from useMarkerData, or null when closed.
 * @param {Function} props.onClose - Callback invoked when the popup is dismissed.
 * @param {boolean} [props.isAdmin=false] - Whether the current user may manually reset the controller.
 * @param {Function} props.onManualReset - Async callback receiving the selected Fire Controller ID.
 * @returns {JSX.Element|null} The selected controller details, or null when closed.
 */
function MarkerPopup({ markerEntry, onClose, isAdmin = false, onManualReset }) {
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!markerEntry) return null;

  const { streetAddr, hydroSystemStatus } = markerEntry;
  const { systemBadge, systemConnection, systemTimeReported, allSensors, relays } = deriveSystemProps(hydroSystemStatus);

  /** Opens the Manual Reset confirmation dialog. */
  function handleManualResetClick() {
    setShowResetConfirmation(true);
  }

  /** Closes the Manual Reset confirmation dialog without dispatching a command. */
  function handleManualResetCancel() {
    setShowResetConfirmation(false);
  }

  /**
   * Dispatches a Manual Reset for the selected marker while preventing duplicate submissions.
   * @returns {Promise<void>} Resolves after the submission attempt completes.
   */
  async function handleManualResetConfirm() {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onManualReset(markerEntry.id);
      setShowResetConfirmation(false);
    } catch {
      // The caller reports the error; keep the confirmation open so the Admin can retry.
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="marker-popup" role="dialog" aria-label={streetAddr ?? 'Fire Controller Details'}>
      <div className="popup-header">
        <span className="popup-header-address">{streetAddr ?? 'Unknown Address'}</span>
        <button className="popup-close-btn" onClick={onClose} aria-label="Close">&#x2715;</button>
      </div>
      <div className="popup-column-headers">
        <span className="popup-column-header-label">Name</span>
        <span className="popup-column-header-label">Connection</span>
        <span className="popup-column-header-label">Status</span>
      </div>
      <div className="popup-scroll-area">
        <div className="popup-section-label">System</div>
        <div className="popup-section-divider" />
        <DeviceRow
          id="System"
          connection={systemConnection}
          statusBadge={systemBadge}
          timestamp={systemTimeReported}
          formatTimestamp={formatTimeReported}
        />
        <DeviceSection label="Sensors" devices={allSensors} resolveName={getSensorName} />
        <DeviceSection label="Relays" devices={relays} resolveName={getRelayName} />
      </div>
      {isAdmin && (
        <div className="popup-footer">
          <div className="popup-section-label popup-admin-section-label">
            {ADMIN_CONTROLS_TITLE}
          </div>
          <div className="popup-section-divider popup-admin-section-divider" />
          <button className="manual-reset-button" type="button" onClick={handleManualResetClick}>
            {MANUAL_RESET_TITLE}
          </button>
        </div>
      )}
      {showResetConfirmation && (
        <ConfirmationPopup
          title={MANUAL_RESET_TITLE}
          message={MANUAL_RESET_MESSAGE}
          onConfirm={handleManualResetConfirm}
          onCancel={handleManualResetCancel}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}

export default MarkerPopup;
